import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProgressBar from '@ramonak/react-progress-bar';
import { Container, Row, Col } from 'react-bootstrap';
import { BackLink, Card, Button, ErrorSummary } from 'nhsuk-react-components';
import { createOrUpdateImplant, loadImplantDto } from '../../actions/implantActions';
import axios from 'axios';
import Papa from 'papaparse';
import ImportMappingModal from './DataImporter/ImportMappingModal';
import {
  formatImportReportSummary,
  loadLastImportReport,
  saveLastImportReport,
} from './DataImporter/lastImportReport';
import { PATIENT_REQUIRED_FIELDS } from './DataImporter/patientImportMapping';
import { rowsToSampleObjects } from './DataImporter/importFieldCatalog';
import { parseCsvTextToRows } from './DataImporter/csvParseUtils';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const emptyModal = {
  visible: false,
  mode: 'patient',
  file: null,
  fileName: '',
  headers: [],
  rowCount: 0,
  sampleRows: [],
  dataRows: [],
};

export default function DataImportExportScreen({ history }) {
  const dispatch = useDispatch();

  const [exportProgress, setExportProgress] = useState(0);
  const [modal, setModal] = useState(emptyModal);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [screenError, setScreenError] = useState(null);
  const [lastPatientReport, setLastPatientReport] = useState(() => loadLastImportReport());
  const [patientMappingDefaults, setPatientMappingDefaults] = useState({
    sourceToTarget: {},
    requiredFields: PATIENT_REQUIRED_FIELDS,
  });

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const implantDto = useSelector((state) => state.implantDto);
  const { loading: implantDtoLoading, error: implantDtoError, implantDto: newImplantDto } = implantDto;

  useEffect(() => {
    document.title = 'Data Import/Export';
    dispatch(loadImplantDto());
  }, [dispatch]);

  useEffect(() => {
    if (!userInfo?.token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/v1/Patient/import/mapping-defaults', {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        if (!cancelled) {
          setPatientMappingDefaults({
            sourceToTarget: data.sourceToTarget || {},
            requiredFields: data.requiredFields || PATIENT_REQUIRED_FIELDS,
          });
        }
      } catch (e) {
        console.error('Failed to load patient mapping defaults', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userInfo?.token]);

  const closeModal = () => {
    if (importing) return;
    setModal(emptyModal);
    setImportResults(null);
    setImportErrors([]);
    setImportProgress(0);
  };

  const openDeviceImportFromFile = async (file) => {
    if (!newImplantDto?.data) {
      alert('Implant DTO still loading — try again in a moment');
      return;
    }
    if (file.size / (1024 * 1024) > 10) {
      alert('File size exceeds the limit of 10 MB');
      return;
    }
    setScreenError(null);
    setImportResults(null);
    setImportErrors([]);

    const text = await file.text();
    const { headers, dataRows } = parseCsvTextToRows(text);
    setModal({
      visible: true,
      mode: 'device',
      file,
      fileName: file.name,
      headers,
      rowCount: dataRows.length,
      sampleRows: rowsToSampleObjects(headers, dataRows, 3),
      dataRows,
    });
  };

  const openPatientImportFromFile = async (file) => {
    if (file.size / (1024 * 1024) > 50) {
      alert('File size exceeds the limit of 50 MB');
      return;
    }
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      alert('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setScreenError(null);
    setImportResults(null);
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post('/api/v1/Patient/import/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setModal({
        visible: true,
        mode: 'patient',
        file,
        fileName: file.name,
        headers: data.headers || [],
        rowCount: data.rowCount || 0,
        sampleRows: data.sampleRows || [],
        dataRows: [],
      });
    } catch (error) {
      setScreenError(error.response?.data?.detail || error.message || 'Preview failed');
    } finally {
      setImporting(false);
    }
  };

  const handleUploadClick = (mode) => (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (mode === 'device') openDeviceImportFromFile(file);
    else openPatientImportFromFile(file);
  };

  const runDeviceImport = async ({ mappings, override }) => {
    setImporting(true);
    setImportProgress(0);
    setImportErrors([]);
    setImportResults(null);

    const headers = modal.headers;
    const rows = modal.dataRows;
    const logs = [];
    let processed = 0;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const csvRow = rows[rowIndex];
      const rowDict = Object.fromEntries(headers.map((header, i) => [header.trim(), (csvRow[i] ?? '').toString().trim()]));
      const newImplant = mappings.reduce((acc, mapping) => {
        if (mapping.dtoProperty === 'id' || mapping.dtoProperty === 'Id') return acc;
        let value = mapping.csvHeader ? rowDict[mapping.csvHeader] : mapping.defaultValue;
        if (mapping.dtoProperty === 'implantStatusChangedDate') {
          let date = new Date(value);
          if (!date || Number.isNaN(date.getTime())) date = new Date();
          value = date.toISOString();
        } else if (
          mapping.dtoProperty === 'implantStatusChanged' ||
          mapping.dtoProperty === 'mrConditional' ||
          mapping.dtoProperty === 'MRConditional'
        ) {
          value = String(value || '').toLowerCase() === 'true' || value === '1';
        }
        acc[mapping.dtoProperty] = value;
        return acc;
      }, {});

      // Override flag kept for parity with patient import UI (API createOrUpdate always upserts by barcode)
      void override;

      try {
        await dispatch(createOrUpdateImplant(newImplant.barcode, newImplant));
      } catch (error) {
        logs.push({
          barcode: newImplant.barcode,
          modelName: newImplant.modelName,
          error: error.message,
        });
      }
      processed += 1;
      setImportProgress(Math.round((processed / rows.length) * 100));
      await sleep(30);
    }

    setImportErrors(logs);
    setImportResults({ processed, errorCount: logs.length });
    setImporting(false);
  };

  const runPatientImport = async ({ duplicateMode, columnMappings }) => {
    if (!modal.file) return;
    setImporting(true);
    setImportProgress(0);
    setScreenError(null);

    // Bulk is one HTTP call — upload finishes in a blink for typical CSVs, so
    // onUploadProgress jumps to "done". Tick slowly while waiting on the server.
    setImportProgress(2);
    const progressTimer = setInterval(() => {
      setImportProgress((p) => {
        if (p >= 90) return 90;
        if (p < 40) return p + 3;
        if (p < 70) return p + 2;
        return p + 1;
      });
    }, 400);

    try {
      const formData = new FormData();
      formData.append('file', modal.file);
      formData.append('duplicateMode', duplicateMode || 'Skip');
      formData.append('columnMappingsJson', JSON.stringify(columnMappings || {}));
      const response = await axios.post('/api/v1/Patient/import/bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      clearInterval(progressTimer);
      setImportProgress(100);
      const report = saveLastImportReport({
        ...response.data,
        fileName: modal.fileName,
      });
      setLastPatientReport(report);
      setImportResults(response.data);
    } catch (error) {
      clearInterval(progressTimer);
      setScreenError(error.response?.data?.detail || error.message || 'Import failed');
    } finally {
      clearInterval(progressTimer);
      setImporting(false);
    }
  };

  const handleStartImport = (payload) => {
    if (modal.mode === 'device') return runDeviceImport(payload);
    return runPatientImport(payload);
  };

  const handleCsvExport = async () => {
    try {
      setExportProgress(0);
      const response = await axios.get('/api/v1/implant/export', {
        headers: { Authorization: `Bearer ${userInfo.token}` },
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setExportProgress(percentCompleted);
        },
      });
      const csvText = await response.data.text();
      const jsonData = Papa.parse(csvText, { header: true }).data;
      const sortedCsvData = Papa.unparse(jsonData, {
        quotes: true,
        quoteChar: '"',
        escapeChar: '"',
        delimiter: ',',
        header: true,
        newline: '\r\n',
      });
      const blob = new Blob([sortedCsvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `implant_list_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExportProgress(0);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col md="12">
              <div className="welcome-box">
                <div className="welcome-title d-flex justify-content-between align-items-center">
                  <h2>Data Import/Export</h2>
                </div>
                <BackLink asElement="a" href="#" onClick={() => history.goBack()}>
                  Return back
                </BackLink>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <Card>
                <Card.Content>
                  <Card.Description>
                    <h3>Import Devices</h3>
                    <p>Upload a CSV of implant registry rows. Map DTO properties to file columns, then import.</p>
                    {implantDtoLoading && <p>Loading implant DTO…</p>}
                    {implantDtoError && (
                      <ErrorSummary>
                        <p>Error loading implant DTO: {implantDtoError}</p>
                      </ErrorSummary>
                    )}
                    <div style={{ marginTop: 16 }}>
                      <input
                        type="file"
                        accept=".csv"
                        disabled={implantDtoLoading || importing}
                        onChange={handleUploadClick('device')}
                      />
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <Card>
                <Card.Content>
                  <Card.Description>
                    <h3>Import Patients with Devices</h3>
                    <p>
                      Upload CSV or Excel. Map PatientDto / ImplantInstanceDto properties (Forename, Manufacturer, …)
                      then import.
                    </p>
                    <div style={{ marginTop: 16 }}>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        disabled={importing}
                        onChange={handleUploadClick('patient')}
                      />
                      {importing && !modal.visible && <p style={{ marginTop: 8 }}>Reading file headers…</p>}
                    </div>
                    {lastPatientReport && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: 12,
                          background: '#f0f4f5',
                          borderLeft: '4px solid #005eb8',
                        }}
                      >
                        <strong>Last import report</strong>
                        <p style={{ margin: '6px 0' }}>{formatImportReportSummary(lastPatientReport)}</p>
                        <p style={{ margin: 0, fontSize: 14 }}>
                          Patients +{lastPatientReport.created || 0} / updated {lastPatientReport.updated || 0} /
                          skipped {lastPatientReport.skipped || 0}
                          {' · '}
                          Devices +{lastPatientReport.devicesCreated || 0} / updated{' '}
                          {lastPatientReport.devicesUpdated || 0}
                          {' · '}
                          Registry +{lastPatientReport.registryCreated || 0}
                          {lastPatientReport.errors?.length > 0 && <> · errors {lastPatientReport.errors.length}</>}
                        </p>
                      </div>
                    )}
                  </Card.Description>
                  {screenError && (
                    <ErrorSummary style={{ marginTop: 16 }}>
                      <p>
                        <strong>Error:</strong> {screenError}
                      </p>
                    </ErrorSummary>
                  )}
                </Card.Content>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <Card>
                <Card.Content>
                  <Card.Description>
                    <h3>Export Devices</h3>
                    <p>Export devices to CSV. You can download up to 50K records</p>
                    <Button onClick={handleCsvExport} disabled={exportProgress > 0}>
                      Export All Devices
                    </Button>
                    {exportProgress > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <ProgressBar completed={exportProgress} />
                        <p>Exporting: {exportProgress}% complete</p>
                      </div>
                    )}
                  </Card.Description>
                </Card.Content>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ImportMappingModal
        visible={modal.visible}
        onCancel={closeModal}
        mode={modal.mode}
        fileName={modal.fileName}
        csvHeaders={modal.headers}
        rowCount={modal.rowCount}
        sampleRows={modal.sampleRows}
        sourceToTarget={patientMappingDefaults.sourceToTarget}
        requiredFields={patientMappingDefaults.requiredFields}
        itemDto={newImplantDto}
        showOverride
        defaultOverride={modal.mode === 'device'}
        overrideLabel={
          modal.mode === 'device'
            ? 'Override existing records based on barcode'
            : 'Override / upsert existing patients and devices (default: skip existing)'
        }
        importing={importing}
        importProgress={importProgress}
        importResults={importResults}
        importErrors={importErrors}
        onStartImport={handleStartImport}
      />
    </React.Fragment>
  );
}
